import type {
  WidgetData,
  UpdateRequest,
  Environment,
  PreviewConfig,
} from '@/types';

const BASE_API_URL: Record<Environment, string> = {
  development: 'https://dev.tomis.tech/api/reviews',
  staging: 'https://staging.tomis.tech/api/reviews',
  production: 'https://app.tomis.tech/api/reviews',
  localhost: 'http://localhost:8000/api/reviews',
};

const getApiUrl = (): string => {
  const env = (process.env.VITE_APP_ENV as Environment) || 'development';
  const url = BASE_API_URL[env];

  if (!url) {
    throw new Error(
      `process.env.VITE_APP_ENV must be one of: ${Object.keys(BASE_API_URL).join(', ')}`
    );
  }

  return url;
};

export const api = async (
  uri: string,
  options: RequestInit = {}
): Promise<Response> => {
  const headers = {
    'Content-Type': 'application/json; charset=utf-8',
    ...(options.headers || {}),
  };

  const response = await fetch(`${getApiUrl()}/${uri}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    throw new Error(
      `API request failed: ${response.status} ${response.statusText}`
    );
  }

  return response;
};

export const update = async (
  body: UpdateRequest,
  providerSlug?: string
): Promise<Response> => {
  const endpoint = providerSlug ? `click/${providerSlug}/` : 'click/';
  return api(endpoint, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
};

// Provider-specific star rating submission
export const submitStarRating = async (
  rating: number,
  message: string | null,
  reminderId: string | number,
  updateData: UpdateRequest,
  providerSlug?: string
): Promise<Response> => {
  return update(
    {
      ...updateData,
      rating,
      message,
      reminder: reminderId,
    },
    providerSlug
  );
};

// Track provider-specific click (for analytics)
export const trackProviderClick = async (
  providerSlug: string,
  reminderId: string | number,
  updateData: UpdateRequest
): Promise<Response> => {
  return update(
    {
      ...updateData,
      reminder: reminderId,
    },
    providerSlug
  );
};

export const getLeaveAReviewWidgetData = async (
  reminder_pk: string | number
): Promise<Response> => {
  return api(`reminders/${reminder_pk}/leave_a_review_data/`, {
    method: 'GET',
  });
};

type DemoApiResponse = {
  success?: boolean;
  data?: WidgetData;
  error?: { message?: string };
};

/** Offline fallback used by preview mode and localhost embed errors. */
export const getFallbackDemoData = (): WidgetData => {
  return {
    business_name: 'TOMIS',
    event: 0,
    person: 0,
    site: 0,
    reminder: 0,
    links: [
      {
        cid: null,
        enabled: true,
        fid: null,
        id: 0,
        page_id: null,
        place_id: null,
        provider: { id: 1, provider: 'Google', slug: 'google' },
        profile_url: null,
        review_submit_url: '#',
      },
      {
        cid: null,
        enabled: true,
        fid: null,
        id: 1,
        page_id: null,
        place_id: null,
        provider: { id: 2, provider: 'TripAdvisor', slug: 'tripadvisor' },
        profile_url: null,
        review_submit_url: '#',
      },
      {
        cid: null,
        enabled: true,
        fid: null,
        id: 2,
        page_id: null,
        place_id: null,
        provider: { id: 3, provider: 'Facebook', slug: 'facebook' },
        profile_url: null,
        review_submit_url: '#',
      },
      {
        cid: null,
        enabled: true,
        fid: null,
        id: 3,
        page_id: null,
        place_id: null,
        provider: { id: 4, provider: 'Yelp', slug: 'yelp' },
        profile_url: null,
        review_submit_url: '#',
      },
    ],
    rating: {
      enabled: true,
      id: 0,
      stars_count: 5,
      stars_cutoff: 4,
      happy_message:
        'We are glad you enjoyed the trip! Please click a link to leave a review.',
      unhappy_contact: 'info@tomis.tech',
      unhappy_form_only: false,
      unhappy_message:
        "We're sorry we didn't meet your expectations. Please let us know how we can improve the TOMIS experience.",
    },
    theme: '#309F87',
  };
};

/** Agency-branded demo payload for `?r=demo`. */
export const getDemoData = async (): Promise<WidgetData> => {
  const response = await api('widget-demo/', { method: 'GET' });
  const payload = (await response.json()) as DemoApiResponse;
  if (!payload.success || !payload.data) {
    throw new Error(payload.error?.message || 'Demo data is unavailable');
  }
  return payload.data;
};

/** Generate widget data from preview configuration */
export const getPreviewData = (config: PreviewConfig): WidgetData => {
  const baseData = getFallbackDemoData();

  // Parse enabled providers
  const enabledProviders = config.enabled_providers
    ? config.enabled_providers
        .split(',')
        .map((p: string) => p.trim().toLowerCase())
    : ['google', 'tripadvisor', 'facebook', 'yelp'];

  // Update links based on enabled providers and custom URLs
  const links = baseData.links.map((link) => {
    const slug = link.provider.slug.toLowerCase();
    let review_submit_url = null;

    // Set custom review URLs if provided
    switch (slug) {
      case 'google':
        review_submit_url = config.google_url || null;
        break;
      case 'yelp':
        review_submit_url = config.yelp_url || null;
        break;
      case 'facebook':
        review_submit_url = config.facebook_url || null;
        break;
      case 'tripadvisor':
        review_submit_url = config.tripadvisor_url || null;
        break;
    }

    return {
      ...link,
      enabled: enabledProviders.includes(slug),
      review_submit_url,
    };
  });

  return {
    ...baseData,
    business_name: config.business_name || baseData.business_name,
    theme: config.theme || baseData.theme,
    site: config.site || baseData.site,
    links,
    rating: {
      ...baseData.rating,
      enabled: config.rating_enabled ?? baseData.rating.enabled,
      stars_cutoff: config.stars_cutoff || baseData.rating.stars_cutoff,
      happy_message: config.happy_message || baseData.rating.happy_message,
      unhappy_message:
        config.unhappy_message || baseData.rating.unhappy_message,
      unhappy_contact:
        config.unhappy_contact || baseData.rating.unhappy_contact,
      unhappy_form_only:
        config.unhappy_form_only ?? baseData.rating.unhappy_form_only,
    },
  };
};
